import {
  Modal, Stack, Text, Group, TextInput, Button, Alert, Card, Avatar, Badge,
} from '@mantine/core'
import { IconAlertTriangle, IconTrash } from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { api } from '@/api/client'
import type { UserRow } from './UsersTable'

interface UserDeleteModalProps {
  opened: boolean
  onClose: () => void
  user: UserRow
  onDeleted: () => void
}

function extractUsername(email: string): string {
  return email.split('@')[0] ?? ''
}

function getInitials(name: string): string {
  return name.split(' ').map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase()
}

export function UserDeleteModal({ opened, onClose, user, onDeleted }: UserDeleteModalProps) {
  const qc = useQueryClient()

  const [confirmText, setConfirmText] = useState('')
  const expectedText = extractUsername(user.email)
  const isMatch = confirmText === expectedText

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await api.delete(`/v1/users/${user.id}`)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] })
      notifications.show({ color: 'teal', title: 'Removido', message: `Usuário ${user.full_name} removido. Dados pessoais anonimizados e acesso revogado.` })
      setConfirmText('')
      onClose()
      onDeleted()
    },
    onError: (err: any) => {
      notifications.show({ color: 'red', title: 'Erro', message: err?.response?.data?.detail ?? 'Falha ao remover usuário.' })
    },
  })

  const handleClose = () => {
    setConfirmText('')
    onClose()
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Remover usuário"
      centered
      size="sm"
    >
      <Stack gap="md">
        {/* Warning */}
        <Alert color="red" icon={<IconAlertTriangle size={16} />} title="Ação irreversível">
          <Text size="sm">
            O usuário será removido: dados pessoais (nome, e-mail) serão anonimizados,
            sessões ativas encerradas e o acesso no Kanidm revogado. Registros de
            auditoria e atribuição em jobs históricos são preservados por exigência legal.
          </Text>
        </Alert>

        {/* User summary */}
        <Card withBorder p="sm">
          <Group gap="sm">
            <Avatar size="md" color="teal" radius="xl">{getInitials(user.full_name)}</Avatar>
            <Stack gap={2}>
              <Text size="sm" fw={600}>{user.full_name}</Text>
              <Text size="xs" c="dimmed">{user.email}</Text>
              <Group gap={4}>
                {user.roles.map((r) => (
                  <Badge key={r} size="xs" variant="light">{r}</Badge>
                ))}
              </Group>
            </Stack>
          </Group>
        </Card>

        {/* Confirmation input */}
        <Stack gap={4}>
          <Text size="sm">
            Digite <strong>{expectedText}</strong> para confirmar:
          </Text>
          <TextInput
            placeholder={expectedText}
            value={confirmText}
            onChange={(e) => setConfirmText(e.currentTarget.value)}
            error={confirmText.length > 0 && !isMatch ? 'Texto não confere' : undefined}
          />
        </Stack>

        {/* Actions */}
        <Group justify="flex-end" gap="xs">
          <Button variant="default" size="sm" onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            color="red"
            size="sm"
            leftSection={<IconTrash size={14} />}
            disabled={!isMatch}
            loading={deleteMutation.isPending}
            onClick={() => deleteMutation.mutate()}
          >
            Remover usuário
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
